/**
 * WizardInput - text input with file upload for wizard conversations.
 *
 * Enter sends, Shift+Enter adds newline. File upload button accepts
 * CSV/XLSX. Shows attached filenames as chips.
 */

import { useRef, useState } from 'react'
import { Paperclip, Send, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

interface WizardInputProps {
  onSend: (content: string) => void
  onUploadFile: (file: File) => Promise<void>
  disabled?: boolean
  isUploading?: boolean
  /** Accepted file types */
  accept?: string
}

export function WizardInput({
  onSend,
  onUploadFile,
  disabled = false,
  isUploading = false,
  accept = '.csv,.xlsx,.xls',
}: WizardInputProps) {
  const [text, setText] = useState('')
  const [pendingFiles, setPendingFiles] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleSend() {
    const trimmed = text.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setText('')
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setPendingFiles((prev) => [...prev, file.name])
    try {
      await onUploadFile(file)
    } finally {
      setPendingFiles((prev) => prev.filter((f) => f !== file.name))
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value)
    // Auto-resize
    const el = e.target
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 160) + 'px'
  }

  return (
    <div className="shrink-0 border-t-[0.5px] border-t-[var(--rule)] px-6 py-4">
      {/* Pending file chips */}
      {pendingFiles.length > 0 && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {pendingFiles.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1.5 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-2.5 py-1 text-sm text-[var(--ink-2)]"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              {name}
            </span>
          ))}
        </div>
      )}
      {/* Input row */}
      <div className="flex items-end gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
        {/* File upload */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[4px] text-[var(--ink-3)] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40"
          title="Upload spreadsheet"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Paperclip className="h-4 w-4" />
          )}
        </Button>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Type a message..."
          rows={1}
          className="min-h-8 max-h-40 flex-1 resize-none bg-transparent py-[5px] text-base leading-relaxed text-[var(--ink)] outline-none disabled:opacity-40"
        />

        {/* Send */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSend}
          disabled={disabled || !text.trim()}
          className={cn(
            'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[4px] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40',
            text.trim() ? 'text-[var(--green)]' : 'text-[var(--ink-3)]',
          )}
          title="Send message"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
